/** Default pause after typing before a DataGrid search is applied. */
export const VLP_GRID_SEARCH_DEBOUNCE_MS = 450;

interface SearchableGrid {
    beginUpdate?: () => void;
    endUpdate?: () => void;
    option: (name: string, value?: unknown) => unknown;
    pageIndex: (value?: number) => number;
    searchByText: (value: string) => void;
}

export interface VlpDataGridSearchController {
    value: string;
    update: (value: unknown) => void;
    apply: () => void;
    reset: () => void;
}

const searchDrafts = new WeakMap<object, string>();
const searchTimers = new WeakMap<object, ReturnType<typeof setTimeout>>();

function cancelScheduledSearch(grid: object): void {
    const timer = searchTimers.get(grid);
    if (timer) clearTimeout(timer);
    searchTimers.delete(grid);
}

function applySearch(grid: SearchableGrid, value: string): void {
    cancelScheduledSearch(grid);
    grid.beginUpdate?.();
    try {
        if (grid.pageIndex() !== 0) grid.pageIndex(0);
        grid.searchByText(value);
    } finally {
        grid.endUpdate?.();
    }
}

/**
 * Keeps the application toolbar search box responsive while avoiding a remote request for every key press.
 * Enter and the toolbar search button call {@link apply}; ordinary typing is applied after a short debounce.
 */
export function createVlpDataGridSearchController(
    grid: SearchableGrid,
    debounceMs = VLP_GRID_SEARCH_DEBOUNCE_MS,
): VlpDataGridSearchController {
    const initialValue = String(grid.option('searchPanel.text') ?? '');
    if (!searchDrafts.has(grid)) searchDrafts.set(grid, initialValue);

    const apply = (): void => applySearch(grid, searchDrafts.get(grid) ?? '');
    const update = (value: unknown): void => {
        searchDrafts.set(grid, String(value ?? ''));
        cancelScheduledSearch(grid);
        searchTimers.set(grid, setTimeout(apply, debounceMs));
    };
    const reset = (): void => {
        searchDrafts.set(grid, '');
        applySearch(grid, '');
    };

    return {
        value: searchDrafts.get(grid) ?? initialValue,
        update,
        apply,
        reset,
    };
}
